import {
  Button,
  Col,
  Divider,
  Dropdown,
  HasAccess,
  MultiLineEllipsisText,
  Row,
  Space,
  SupplierStatus,
  Tag,
  Typography,
} from 'components';
import moment from 'moment';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { DownOutlined } from '@ant-design/icons';
import { EditIcon } from 'components/common/icons';
import { PERMISSIONS } from 'constants/permissions';
import { PurchaseOrderStatusBadge } from 'components/common/StatusBadge';
import { PurchaseOrderStatusTransitions, PurchaseOrderStatusType } from 'models/PurchaseOrder';
import { AssignmentEditModal } from './assignment/edit';
import { ShippingEditModal } from './shipping/edit';
import { ConfirmOrderModal } from './confirmOrder';
import styles from './styles.module.scss';
import { useDetails } from './useDetails';

const { Text, Title } = Typography;

export const Details = React.memo(() => {
  const {
    purchaseOrder,
    assignments,
    handleStatusChange,
    assignmentModalState,
    onAssignmentCancel,
    onAssignmentOpen,
    shippingModalState,
    onShippingCancel,
    onShippingOpen,
    confirmOrderModalState,
    onConfirmOrderCancel,
  } = useDetails();

  const changeStatusMenu = useMemo(() => {
    const currentStatus = purchaseOrder.status as PurchaseOrderStatusType;
    if (!currentStatus) return [];
    const availableOptions = PurchaseOrderStatusTransitions[currentStatus] || [];
    return availableOptions.map((status) => ({
      label: status,
      key: status,
    }));
  }, [purchaseOrder.status]);

  const isInWarehouse = purchaseOrder.status === 'IN_WAREHOUSE';
  const hasAction = !changeStatusMenu?.length;

  const shippingInformation = purchaseOrder.shippingInformation || {};

  return (
    <>
      <Row gutter={[0, 20]}>
        {/* Order ID Section */}
        <Col span={24}>
          <Row gutter={[0, 8]} justify={'space-between'}>
            <Col span={24}>
              <Row wrap={false} align={'middle'} className={'gap-4'}>
                <Col flex="0 1 auto">
                  <Title level={4} ellipsis={{ tooltip: true }} className={'font-weight-800 reset-title'}>
                    {purchaseOrder.uuid}
                  </Title>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[12, 12]} align={'middle'} justify={'space-between'}>
                <Col>
                  <PurchaseOrderStatusBadge
                    type={(purchaseOrder.status || 'PROCESSING') as Exclude<PurchaseOrderStatusType, 'ALL'>}
                  />
                </Col>
                <Col>
                  <HasAccess role={PERMISSIONS.purchaseOrders.purchaseOrder.write}>
                    <Dropdown
                      menu={{
                        items: changeStatusMenu.map((item) => ({
                          label: <PurchaseOrderStatusBadge type={item.key as PurchaseOrderStatusType} />,
                          key: item.key,
                        })),
                        onClick: ({ key }) => handleStatusChange(key as PurchaseOrderStatusType),
                      }}
                      trigger={['click']}
                    >
                      <Button disabled={isInWarehouse || hasAction} type={'primary'}>
                        <Space>
                          Change Status
                          <DownOutlined />
                        </Space>
                      </Button>
                    </Dropdown>
                  </HasAccess>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row gutter={[0, 8]}>
            <Col span={24}>
              <Row style={{ gap: '9px', marginTop: 10 }} justify={'space-between'}>
                <Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                  {purchaseOrder.formattedCreatedDate}
                </Text>
                <Text className={'font-size-12'} style={{ color: '#99A0AE' }}>
                  {purchaseOrder.formattedLastModifiedDate}
                </Text>
              </Row>
            </Col>
          </Row>
        </Col>

        <Divider className={styles.divider} />

        {/* Assignment Section */}
        <Col span={24}>
          <Row gutter={[0, 8]}>
            <Col span={24}>
              <Row align={'middle'} justify={'space-between'}>
                <Col>
                  <Text className={'font-weight-600 font-size-16'}>Assignments</Text>
                </Col>
                <Col>
                  <HasAccess role={PERMISSIONS.purchaseOrders.purchaseOrder.write}>
                    {!isInWarehouse && (
                      <Button icon={<EditIcon />} type="text" size={'small'} onClick={onAssignmentOpen} />
                    )}
                  </HasAccess>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[0, 8]}>
                <Col span={24}>
                  <Row wrap={false} className={'gap-6'}>
                    <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                      Post Sales Manager:
                    </Text>
                    <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                      {assignments?.postSalesManager
                        ? `${assignments.postSalesManager.firstName} ${assignments.postSalesManager.lastName}`
                        : 'Not Set'}
                    </Text>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row wrap={false} className={'gap-6'}>
                    <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                      Post Sales Manager Assistant:
                    </Text>
                    <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                      {assignments?.postSalesManagerAssistant
                        ? `${assignments.postSalesManagerAssistant.firstName} ${assignments.postSalesManagerAssistant.lastName}`
                        : 'Not Set'}
                    </Text>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>

        <Divider className={styles.divider} />

        {/* Supplier Section */}
        <Col span={24}>
          <Row gutter={[0, 8]}>
            <Col span={24} className={'margin-b-4'}>
              <Typography.Text className={'font-weight-600 font-size-16'} style={{ color: '#05796B' }}>
                Supplier Information
              </Typography.Text>
            </Col>
            <Col span={24}>
              <Link
                target={'_blank'}
                to={`${ADMIN_ROUTES.suppliers.view}/${purchaseOrder.supplier?.id}/general`}
                className={'underline-button'}
              >
                <Text underline className={'font-weight-600 font-size-16'} ellipsis={{ tooltip: true }}>
                  {purchaseOrder.supplier?.name || 'No Supplier Assigned'}
                </Text>
              </Link>
            </Col>
            {purchaseOrder.supplier && (
              <>
                <Col span={24}>
                  <Row gutter={4} align={'middle'} wrap={false}>
                    {purchaseOrder.supplier.labels?.map((label: string, index: number) => (
                      <Col key={index}>
                        <Text className={styles.option_tag} ellipsis={{ tooltip: true }}>
                          {label}
                        </Text>
                      </Col>
                    ))}
                  </Row>
                </Col>
                <Col span={24}>
                  <Row wrap={false} className={'gap-6'}>
                    <SupplierStatus status={purchaseOrder.supplier.status} tooltip={purchaseOrder.supplier.reason} />
                  </Row>
                </Col>
                <Col span={24}>
                  <Row gutter={6}>
                    {purchaseOrder.supplier.tags?.map((tag: string, index: number) => (
                      <Col key={index + tag}>
                        <Tag title={tag} className={'custom-tag'}>
                          {tag}
                        </Tag>
                      </Col>
                    ))}
                  </Row>
                </Col>
                <Col span={24}>
                  <MultiLineEllipsisText
                    maxLines={3}
                    subTitle={'Additional Notes:'}
                    text={purchaseOrder.supplier.additionalNotes}
                  />
                </Col>
              </>
            )}
          </Row>
        </Col>
        <Divider className={styles.divider} />
        {/* Shipping Information */}
        <Col span={24}>
          <Row gutter={[0, 8]}>
            <Col span={24}>
              <Row align={'middle'} justify={'space-between'}>
                <Col>
                  <Text className={'font-weight-600 font-size-16'}>Shipping Information</Text>
                </Col>
                <Col>
                  <HasAccess role={PERMISSIONS.purchaseOrders.purchaseOrder.write}>
                    {!isInWarehouse && (
                      <Button icon={<EditIcon />} type="text" size={'small'} onClick={onShippingOpen} />
                    )}
                  </HasAccess>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[0, 8]}>
                <Col span={24}>
                  <Row wrap={false} justify={'space-between'} className={'gap-6'}>
                    <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                      Shipping Type
                    </Text>
                    <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                      {shippingInformation.shippingType || 'Not Set'}
                    </Text>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row wrap={false} justify={'space-between'} className={'gap-6'}>
                    <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                      Shipping Cost
                    </Text>
                    <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                      {shippingInformation.shippingCost ? `€${shippingInformation.shippingCost}` : 'Not Set'}
                    </Text>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row wrap={false} justify={'space-between'} className={'gap-6'}>
                    <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                      Confirmed Shipping Date
                    </Text>
                    <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                      {shippingInformation.shippingDate
                        ? `${moment(shippingInformation.shippingDate).format('DD.MM.YYYY')}`
                        : 'Not Set'}
                    </Text>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row wrap={false} justify={'space-between'} className={'gap-6'}>
                    <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                      Expected Arrival Date
                    </Text>
                    <Text type={'secondary'} ellipsis={{ tooltip: true }}>
                      {shippingInformation.expectedArrivalDate
                        ? `${moment(shippingInformation.expectedArrivalDate).format('DD.MM.YYYY')}`
                        : 'Not Set'}
                    </Text>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row wrap={false} justify={'space-between'} className={'gap-6'}>
                    <Text type={'secondary'} style={{ whiteSpace: 'nowrap' }}>
                      Shipping Address
                    </Text>
                    <Text type={'secondary'}>
                      {shippingInformation.country?.name ||
                      shippingInformation.streetAddress ||
                      shippingInformation.city ||
                      shippingInformation.state ||
                      shippingInformation.zip
                        ? `${shippingInformation.country?.name || ''}, ${shippingInformation.state || ''}, ${shippingInformation.city || ''}, ${shippingInformation.streetAddress || ''},  ${shippingInformation.zip || ''}`
                            .replace(/^,\s*|,\s*$|,\s*,/g, ', ')
                            .trim()
                        : 'Not Set'}
                    </Text>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
      <AssignmentEditModal open={assignmentModalState} onCancel={onAssignmentCancel} />
      <ShippingEditModal open={shippingModalState} onCancel={onShippingCancel} />
      <ConfirmOrderModal open={confirmOrderModalState} onCancel={onConfirmOrderCancel} />
    </>
  );
});
